import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { Download, Trash2, Eye, Search } from "lucide-react";
import { formatARS, groupAR } from "@/lib/money";

const money = (n) => formatARS(n); // Fix BR: $ 1.234.567,89
const MONTHS = ["", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-AR") : "—");

export function HistoryTab({ token }) {
  const headers = { "X-Budget-Pin-Token": token };
  const [query, setQuery] = useState("");
  const [list, setList] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async (q) => {
    try { const { data } = await api.get("/budget/list", { headers, params: q ? { q } : {} }); setList(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const search = (e) => { e.preventDefault(); load(query); };

  const view = async (id) => {
    try { const { data } = await api.get(`/budget/${id}`, { headers }); setDetail(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este presupuesto guardado?")) return;
    try { await api.delete(`/budget/${id}`, { headers }); toast.success("Presupuesto eliminado"); setDetail(null); load(query); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/budget/${id}/print.pdf`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `presupuesto_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: detail ? "1fr 1fr" : "1fr", gap: 18 }}>
      <div>
        <form onSubmit={search} style={{ display: "flex", gap: 8, marginBottom: 12 }} data-testid="budget-history-search-form">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nombre de objetivo o número..." data-testid="budget-history-search-input" style={{ flex: 1 }}/>
          <button type="submit" className="btn-ghost" style={{ padding: "8px 14px" }} data-testid="budget-history-search-btn"><Search size={14}/></button>
        </form>
        <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="budget-history-list">
          {list == null && <div style={{ padding: 20 }}><div className="skel" style={{ height: 60 }} /></div>}
          {list && list.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>No se encontraron presupuestos.</div>}
          {list && list.map((q) => (
            <div key={q.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", alignItems: "center", gap: 10 }} data-testid={`budget-history-row-${q.id}`}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Nº {q.number ?? "—"} · {q.name}</div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{q.objective_label} · {MONTHS[q.month]} {q.year} · {new Date(q.created_at).toLocaleDateString("es-AR")} · {q.created_by_name}</div>
              </div>
              <div style={{ fontWeight: 900, fontSize: 14, color: "#0A0A0A" }}>{money(q.precio_final)}</div>
              <button onClick={() => view(q.id)} className="btn-ghost" style={{ padding: "6px 10px" }} data-testid={`budget-history-view-${q.id}`}><Eye size={14}/></button>
              <button onClick={() => downloadPdf(q.id)} className="btn-ghost" style={{ padding: "6px 10px" }} data-testid={`budget-history-pdf-${q.id}`}><Download size={14}/></button>
              <button onClick={() => del(q.id)} className="btn-ghost" style={{ padding: "6px 10px", color: "var(--invict-red)", borderColor: "var(--invict-red)" }} data-testid={`budget-history-delete-${q.id}`}><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      </div>

      {detail && (
        <div className="card" style={{ padding: 18 }} data-testid="budget-history-detail">
          <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>Nº {detail.number ?? "—"} · {detail.name}</h3>
          <p style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>{detail.objective_label} · {MONTHS[detail.month]} {detail.year} · Válido hasta {fmtDate(detail.valid_until)}</p>
          {detail.shifts.map((s, i) => (
            <div key={s.id || i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", borderBottom: "1px solid var(--invict-bone-3)" }}>
              <span>{s.label} ({s.time_from}–{s.time_to})</span><span>{s.total_hours_month} h · {money(s.subtotal)}</span>
            </div>
          ))}
          {(() => {
            // Fix BU (2026-11-04): horas del mes y valor de la hora sin y con IVA (los viejos, con sus totales).
            const h = detail.total_hours_month || 0;
            const sinIva = detail.client_hourly_rate ?? (h ? detail.total_sin_iva / h : 0);
            const conIva = detail.client_hourly_rate_con_iva ?? (h ? (detail.precio_final_con_iva ?? detail.precio_final) / h : 0);
            return (
              <div style={{ fontSize: 12, padding: "6px 0", display: "flex", flexDirection: "column", gap: 2 }} data-testid="budget-history-hours">
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Horas del mes</span><span>{groupAR(h) || "0"} h</span></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Valor de la hora sin IVA</span><span>{money(sinIva)}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Valor de la hora con IVA</span><span>{money(conIva)}</span></div>
              </div>
            );
          })()}
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "6px 0" }}>
            <span>Total sin IVA</span><span>{money(detail.total_sin_iva)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 17, fontWeight: 900, marginTop: 6, paddingTop: 10, borderTop: "2px solid #0A0A0A" }}>
            <span>PRECIO FINAL (con IVA)</span><span>{money(detail.precio_final_con_iva ?? detail.precio_final)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
